
interface ReviewProps {
  author: string;
  reviewBody: string;
  datePublished: string;
  rating?: number;
}

interface ItemReviewedProps {
  name: string;
  // Only include Google-supported types for reviews
  // LocalBusiness is the correct type for nursing homes
  type: "LocalBusiness" | "Product" | "Organization";
  url?: string;
  identifier?: string;
}

/**
 * Generate Review schema for testimonials with proper structure
 * for Google structured data validation
 * @param reviews Array of reviews
 * @param itemReviewed The reviewed entity (must be one of the Google-supported types)
 */
export function generateReviewSchema(reviews: ReviewProps[], itemReviewed: ItemReviewedProps) {
  // Ensure URL is absolute
  const itemUrl = itemReviewed.url && !itemReviewed.url.startsWith('http') 
    ? `https://www.grandpal.co${itemReviewed.url}`
    : itemReviewed.url;

  // Calculate aggregate rating if ratings are available
  const ratingsAvailable = reviews.some(review => review.rating !== undefined);
  let aggregateRating = null;
  
  if (ratingsAvailable) {
    const validRatings = reviews.filter(r => r.rating !== undefined).map(r => r.rating as number);
    const ratingSum = validRatings.reduce((sum, rating) => sum + rating, 0);
    const ratingCount = validRatings.length;
    const ratingAverage = ratingSum / ratingCount;
    
    aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": ratingAverage.toFixed(1),
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": ratingCount
    };
  }

  // Generate unique identifier for the item
  const uniqueId = itemReviewed.identifier || 
    (itemUrl ? itemUrl : `https://www.grandpal.co/item/${itemReviewed.name.toLowerCase().replace(/\s+/g, '-')}`);

  // Create the review items
  const reviewItems = reviews.map(review => ({
    "@type": "Review",
    "author": {
      "@type": "Person",
      "name": review.author
    },
    "reviewBody": review.reviewBody,
    "datePublished": review.datePublished,
    ...(review.rating && {
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating,
        "bestRating": "5",
        "worstRating": "1"
      }
    })
  }));

  // Create the main schema object based on the itemReviewed type
  const schemaObject = {
    "@context": "https://schema.org",
    "@type": itemReviewed.type,
    "@id": uniqueId,
    "name": itemReviewed.name,
    ...(itemUrl && { "url": itemUrl }),
    ...(aggregateRating && { "aggregateRating": aggregateRating }),
    ...(reviews.length > 0 && { "review": reviewItems })
  };

  return schemaObject;
}
